import { ChipFilter } from '@/pages/AdvancedEditor/components/panels/common/ChipFilter';
import { AvatarFilter, AvatarFilterDeprecated } from '@/pages/avatar';
import { useAllAvatars } from '@/pages/avatar/hooks/useAllAvatars';
import { useFlag } from '@/services/featureFlags';
import { PresenterUsecase, isExpressPresenter, isPremiumPresenter, isV2Presenter } from '@d-id/types';
import styled from '@emotion/styled';
import { useMemo } from 'react';

const StyledChipFilter = styled(ChipFilter)`
    margin-top: 6px;
    margin-bottom: 2px;
`;

interface Props {
    filter: AvatarFilter | AvatarFilterDeprecated;
    setFilter: (filter: AvatarFilter | AvatarFilterDeprecated) => void;
    maxWidth?: string;
}

export const AvatarTabs = ({ filter, setFilter, maxWidth }: Props) => {
    const { userAvatars, allAvatars } = useAllAvatars(PresenterUsecase.Video, 'all', true);
    const newAvatarsFiltersEnabled = useFlag('new_avatars_filters', false);

    const shouldShowExpressTab = useMemo(() => userAvatars.some(isExpressPresenter), [userAvatars]);
    const shouldShowPremiumTab = useMemo(
        () => allAvatars.some(presenter => isPremiumPresenter(presenter) && !isV2Presenter(presenter.id)),
        [userAvatars]
    );

    const handleTabChange = (id: string) => {
        setFilter(id as AvatarFilter | AvatarFilterDeprecated);
    };

    const tabs = [
        {
            id: AvatarFilter.All,
            label: 'All',
            active: filter === AvatarFilter.All,
            dataTestId: 'avatar-tab-filter-all',
        },
        {
            id: AvatarFilter.MyAvatars,
            label: 'My Avatars',
            active: filter === AvatarFilter.MyAvatars,
            hide: !userAvatars.length,
            dataTestId: 'avatar-tab-filter-my-avatars',
        },
        {
            id: AvatarFilter.VideoAvatars,
            label: 'Video Avatars',
            active: filter === AvatarFilter.VideoAvatars,
            dataTestId: 'avatar-tab-filter-video-avatars',
        },
        {
            id: AvatarFilter.PhotoAvatars,
            label: 'Photo Avatars',
            active: filter === AvatarFilter.PhotoAvatars,
            dataTestId: 'avatar-tab-filter-photo-avatars',
        },
    ];

    const deprecatedTabs = [
        {
            id: AvatarFilterDeprecated.All,
            label: 'All',
            active: filter === AvatarFilterDeprecated.All,
            dataTestId: 'avatar-tab-filter-all',
        },
        {
            id: AvatarFilterDeprecated.MyAvatars,
            label: 'My Avatars',
            active: filter === AvatarFilterDeprecated.MyAvatars,
            hide: !userAvatars.length,
            dataTestId: 'avatar-tab-filter-my-avatars',
        },
        {
            id: AvatarFilterDeprecated.PremiumPlus,
            label: 'Premium +',
            active: filter === AvatarFilterDeprecated.PremiumPlus,
            dataTestId: 'avatar-tab-filter-premium_plus',
        },
        ...(shouldShowExpressTab
            ? [
                  {
                      id: AvatarFilterDeprecated.Express,
                      label: 'Express',
                      active: filter === AvatarFilterDeprecated.Express,
                      dataTestId: 'avatar-tab-filter-express',
                  },
              ]
            : []),
        ...(shouldShowPremiumTab
            ? [
                  {
                      id: AvatarFilterDeprecated.Premium,
                      label: 'Premium',
                      active: filter === AvatarFilterDeprecated.Premium,
                      dataTestId: 'avatar-tab-filter-premium',
                  },
              ]
            : []),
        {
            id: AvatarFilterDeprecated.Standard,
            label: 'Standard',
            active: filter === AvatarFilterDeprecated.Standard,
            dataTestId: 'avatar-tab-filter-standard',
        },
    ];

    return (
        <StyledChipFilter
            tabs={newAvatarsFiltersEnabled ? tabs : deprecatedTabs}
            maxWidth={maxWidth}
            activeTab={filter}
            onTabChange={handleTabChange}
        />
    );
};
